/**
 * Auth Background Effects - Animated gradient, light orbs, floating currency
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useMemo, useState, useEffect } from 'react';
import { motion, useReducedMotion } from 'framer-motion';

const SYMBOLS = ['$', '€', '£'] as const;

function buildMoneyItems(count: number) {
  const items: { symbol: string; top: number; left: number; size: number; duration: number; delay: number }[] = [];
  for (let i = 0; i < count; i++) {
    items.push({
      symbol: SYMBOLS[i % 3],
      top: (i * 7 + 5) % 92,
      left: (i * 11 + 3) % 92,
      size: 3 + (i % 5),
      duration: 12 + (i % 9),
      delay: i * 0.6,
    });
  }
  return items;
}

export const AuthBackgroundEffects: FC = () => {
  const noMotion = !!useReducedMotion();
  const [moneyCount, setMoneyCount] = useState(24);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    setMoneyCount(mq.matches ? 12 : 24);
    const h = () => setMoneyCount(mq.matches ? 12 : 24);
    mq.addEventListener('change', h);
    return () => mq.removeEventListener('change', h);
  }, []);

  const moneyItems = useMemo(() => buildMoneyItems(moneyCount), [moneyCount]);

  const orbConfigs = useMemo(
    () => [
      { y: [0, -20, 0] as [number, number, number], duration: 6 },
      { y: [0, 15, 0] as [number, number, number], duration: 5 },
      { y: [0, -12, 0] as [number, number, number], duration: 7 },
      { y: [0, 10, 0] as [number, number, number], duration: 6 },
      { y: [0, -8, 0] as [number, number, number], duration: 8 },
    ],
    [],
  );

  const orbVariants = (i: number) =>
    noMotion
      ? { hidden: { opacity: 0 }, visible: { opacity: 1 } }
      : {
          hidden: { opacity: 0, scale: 0.8 },
          visible: {
            opacity: 1,
            scale: 1,
            y: orbConfigs[i].y as [number, number, number],
            transition: {
              opacity: { duration: 0.8, delay: 0.1 * i },
              scale: { duration: 0.8, delay: 0.1 * i },
              y: {
                duration: orbConfigs[i].duration,
                repeat: Infinity,
                ease: 'easeInOut' as const,
                delay: 0.5 + 0.2 * i,
              },
            },
          },
        };

  const moneyVariants = (d: number, delay: number) =>
    noMotion
      ? { hidden: { opacity: 0 }, visible: { opacity: 0.25 } }
      : {
          hidden: { opacity: 0 },
          visible: {
            opacity: [0.15, 0.35, 0.15],
            y: [0, -50, 0],
            rotate: [0, 10, 0],
            transition: {
              opacity: { duration: d, repeat: Infinity, ease: 'easeInOut' as const, delay },
              y: { duration: d, repeat: Infinity, ease: 'easeInOut' as const, delay },
              rotate: { duration: d * 1.2, repeat: Infinity, ease: 'easeInOut' as const, delay },
            },
          },
        };

  const gradientStyle = useMemo(
    () => ({
      background: `linear-gradient(135deg, hsl(var(--primary) / 0.12) 0%, hsl(var(--background)) 45%, hsl(var(--secondary) / 0.12) 100%)`,
      backgroundSize: '200% 200%',
    }),
    [],
  );

  const gradientAnimate = noMotion
    ? { opacity: 1 }
    : { opacity: 1, backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'] as [string, string, string] };

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={gradientAnimate}
        transition={
          noMotion
            ? { duration: 0 }
            : { opacity: { duration: 0.8 }, backgroundPosition: { duration: 22, repeat: Infinity, ease: 'easeInOut' } }
        }
        className="absolute inset-0 z-0"
        style={gradientStyle}
        aria-hidden
      />
      {[
        { cls: 'top-1/4 left-1/4 w-72 h-72 md:w-[28rem] md:h-[28rem] bg-primary/25' },
        { cls: 'bottom-1/4 right-1/4 w-64 h-64 md:w-80 md:h-80 bg-success/20' },
        { cls: 'top-1/2 right-1/3 w-48 h-48 md:w-64 md:h-64 bg-primary/18' },
        { cls: 'top-1/3 right-1/4 w-40 h-40 md:w-52 md:h-52 bg-secondary/25' },
        { cls: 'bottom-1/3 left-1/3 w-56 h-56 md:w-72 md:h-72 bg-primary/12' },
      ].map(({ cls }, i) => (
        <motion.div
          key={i}
          variants={orbVariants(i)}
          initial="hidden"
          animate="visible"
          className={`absolute z-0 rounded-full blur-3xl ${cls}`}
          style={
            !noMotion
              ? { boxShadow: '0 0 100px 50px hsl(var(--primary) / 0.1)' }
              : undefined
          }
          aria-hidden
        />
      ))}
      <div
        className="pointer-events-none absolute inset-0 z-[1] overflow-hidden"
        aria-hidden
      >
        {moneyItems.map((item, i) => (
          <motion.span
            key={i}
            variants={moneyVariants(item.duration, item.delay)}
            initial="hidden"
            animate="visible"
            className="absolute font-bold select-none"
            style={{
              top: `${item.top}%`,
              left: `${item.left}%`,
              fontSize: `${item.size}rem`,
              color: 'hsl(var(--primary))',
            }}
          >
            {item.symbol}
          </motion.span>
        ))}
      </div>
    </>
  );
}
